import { useCallback /* useState */ } from 'react';
import { IconButton } from '@/ui/common/Buttons/IconButton';
import { ErrorStatusIcon } from '@/ui/common/Icons/ErrorStatusIcon';
import { InfoStatusIcon } from '@/ui/common/Icons/InfoStatusIcon';
import { SuccessStatusIcon } from '@/ui/common/Icons/SuccessStatusIcon';
import { cn } from '@/service/utils/cn';
import { scrollToId, scrollToIdWithMargin } from '@/service/utils/scrollToId';
import { useAvailabilityStore } from '@/service/state/Availability/useAvailabilityStore';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { useWindowWidth } from '@/service/hooks/useWindowWidth';
import { Tooltip } from '@/ui/common/Tooltip/Tooltip';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
/* import { PromoCodeInput } from '@/ui/common/PromoCodeInput/PromoCodeInput'; */

type TStickyStatusBarProps = {
    partnerSlug: string;
    className?: string;
    totalSum?: number;
    includesTaxes?: boolean;
    setOptionsTouched?: () => void;
};

export const StickyStatusBar = ({
    className,
    totalSum,
    includesTaxes,
    setOptionsTouched,
}: TStickyStatusBarProps) => {
    const { isLightframePage } = usePartnerStore();
    const status = useAvailabilityStore((state) => state.status);
    /* const [showPromo, setShowPromo] = useState(false); */

    const { mobile } = useWindowWidth();

    const handleScrollingIntoView = useCallback(() => {
        setOptionsTouched?.();

        isLightframePage
            ? scrollToId(status.link, 'center')
            : scrollToIdWithMargin(status.link, { parentId: 'tickets-container' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLightframePage, mobile, status.link]);

    const completed = status.isValid && !status.error;

    return (
        <div
            className={cn(
                `fixed bottom-0 z-50 flex w-full items-center rounded-t-[15px] border-[0.6px] px-6 py-3 shadow-popup md:sticky md:h-[60px] md:px-[69px] ${
                    status.isValid && status.touched && !status.error
                        ? 'border-green-1 bg-green-2 text-state-success dark:border-green-3 dark:bg-green-4'
                        : status.error
                          ? 'border-red-9 bg-red-10 text-state-error dark:border-red-11 dark:bg-red-12'
                          : status.message
                            ? 'border-blue-13 bg-blue-14 text-state-info dark:bg-blue-15'
                            : ''
                }`,
                className,
                completed && 'h-[135px] flex-col md:h-[90px] md:flex-row md:justify-between',
                completed && !includesTaxes && 'h-20 md:h-16'
            )}
            data-test-id="tickets-status-bar"
        >
            <div>
                <IconButton
                    className="tx-large-semibold gap-2 text-left font-semibold underline md:font-medium"
                    icon={
                        <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center md:h-9 md:w-9">
                            {(completed && <SuccessStatusIcon />) ||
                                (status.error && <ErrorStatusIcon />) ||
                                (!!status.message && <InfoStatusIcon />)}
                        </div>
                    }
                    label={status.message}
                    onClick={handleScrollingIntoView}
                />
                {/* {completed &&
                    (showPromo ? (
                        <PromoCodeInput />
                    ) : (
                        <p
                            className="tx-large-medium absolute bottom-3 left-0 w-full cursor-pointer text-center text-gray-1 underline dark:text-white md:static md:ml-11 md:text-left"
                            onClick={() => setShowPromo(true)}
                        >
                            Have a promo code?
                        </p>
                    ))} */}
            </div>
            {completed && (
                <div className="relative flex w-full flex-col items-center border-b border-green-1 pb-1 text-gray-2 dark:border-green-5 dark:text-gray-3 md:h-full md:w-auto md:border-b-0 md:border-l md:pl-4 md:pt-2">
                    <p className="tx-large-medium">
                        Total price:&nbsp;
                        <span className="tx-heading-4 text-gray-1 dark:text-white">
                            {getFormattedPrice(totalSum || '')}
                        </span>
                    </p>
                    {includesTaxes && (
                        <Tooltip
                            placement="right"
                            tooltipClassName="!md:right-[30px] "
                            text={
                                'Taxes and fees include local and state taxes (when applicable) and a service fee for facilitating your booking. This ensures a high level of traveler services, and secure processing'
                            }
                        >
                            <span className="tx-medium-medium text-[11px] underline decoration-dotted md:tx-normal-medium">
                                Includes Taxes & Fees
                            </span>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};
